import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {assignProfile, clearDeviceSettings, fetchProfiles} from '@/entities/device/api';
import type {SettingsProfile} from '@/entities/device/types';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {StatusPill} from '@/shared/ui/status';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Apply a named preset to this terminal. Applying clears the per-device override first,
 * then points the device at the profile — otherwise a prior override would keep masking
 * the profile and "apply" would look like it did nothing. The next sync pushes it down.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  empty: {paddingVertical: spacing(2)},
  row: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  name: {flex: 1},
});

export function ProfilePicker({
  companyId,
  deviceId,
  assignedProfileId,
}: {
  companyId: string;
  deviceId: string;
  assignedProfileId?: string | null;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const profilesQuery = useQuery({
    queryKey: ['profiles', companyId],
    queryFn: () => fetchProfiles(companyId),
  });

  const apply = useMutation({
    meta: {toast: 'toast.profileApplied', toastError: true},
    mutationFn: async (profileId: string) => {
      await clearDeviceSettings(companyId, deviceId);
      await assignProfile(companyId, [deviceId], profileId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['device', companyId, deviceId]});
    },
  });

  if (profilesQuery.isPending) {
    return <Skeleton height={110} radius={radii.lg} />;
  }

  const profiles: SettingsProfile[] = profilesQuery.data ?? [];
  if (profiles.length === 0) {
    return (
      <AppText variant="body" color={p.mutedForeground} style={styles.empty}>
        {t('terminal.noProfiles')}
      </AppText>
    );
  }

  return (
    <View style={styles.root}>
      {profiles.map(profile => {
        const active = assignedProfileId === profile.profile_id;
        return (
          <Card
            key={profile.profile_id}
            padding={4}
            style={[styles.row, active && {borderColor: p.primary, borderWidth: 1}]}>
            <View style={styles.head}>
              <AppText variant="heading" style={styles.name} numberOfLines={1}>
                {profile.name}
              </AppText>
              {active ? (
                <StatusPill tone="online" size="sm" label={t('terminal.profileActive')} />
              ) : null}
            </View>
            {profile.description ? (
              <AppText variant="caption">{profile.description}</AppText>
            ) : null}
            <AppButton
              title={t(active ? 'terminal.profileReapply' : 'terminal.profileApply')}
              // The applied profile is already in force: re-applying is a repair, not
              // the action the screen invites — so it goes quiet and the others stay
              // affirmative.
              tone={active ? 'neutral' : 'soft'}
              size="sm"
              loading={apply.isPending && apply.variables === profile.profile_id}
              disabled={apply.isPending}
              onPress={() => apply.mutate(profile.profile_id)}
            />
          </Card>
        );
      })}
    </View>
  );
}
